import sumBy from 'lodash/sumBy';

import {
  AVERAGE_HOURS_IN_MONTH,
  HOURS_IN_DAY,
  SECONDS_IN_HOUR,
} from '@cast/constants';
import { CostReportSummary } from '@cast/types';
import { toBig } from '@cast/utils';

import { emptySummary } from 'features/cluster/cost-report/consts';
import {
  AggregatedMetrics,
  CostReportDataByPeriod,
  HourlyClusterCostReportData,
  HourlyCostPerResource,
} from 'features/cost-report/types/costOverTime';
import {
  hasFallbackDataPoint,
  isPastDataPoint,
} from 'features/cost-report/utils/costOverTime';
import {
  getAverageRates,
  isCurrentDayDatapoint,
} from 'features/cost-report/utils/metrics/aggregateCostOverTimeMetrics';
import { DataGranularity } from 'types/cost-report';
import { granularityToSeconds } from 'utils/costReport';
import { safeDivide } from 'utils/safeDivide';

export const emptyDataPoint: Omit<
  HourlyClusterCostReportData,
  'timestamp' | 'generated' | 'forecast'
> = {
  totalCpu: 0,
  totalRamGib: 0,
  totalGpu: 0,
  totalStorage: 0,
  totalCost: 0,
  totalCpuCost: 0,
  totalRamCost: 0,
  totalGpuCost: 0,
  totalStorageCost: 0,
  normalizedCostPerCpu: 0,
  normalizedCostPerGib: 0,
  normalizedCostPerGpu: 0,
  normalizedCostPerStorage: 0,
  costPerStorage: 0,

  onDemandCost: 0,
  onDemandCostPerCpu: 0,
  onDemandCostPerGib: 0,
  onDemandCpuCount: 0,
  onDemandUptime: 0,
  onDemandCpuCost: 0,
  onDemandRamCost: 0,
  onDemandRamGib: 0,
  onDemandCostPerGpu: 0,
  onDemandGpuCount: 0,
  onDemandGpuCost: 0,

  fallbackCost: 0,
  fallbackCostPerCpu: 0,
  fallbackCostPerGib: 0,
  fallbackCpuCount: 0,
  fallbackUptime: 0,
  fallbackCpuCost: 0,
  fallbackRamCost: 0,
  fallbackRamGib: 0,
  fallbackCostPerGpu: 0,
  fallbackGpuCount: 0,
  fallbackGpuCost: 0,

  spotCost: 0,
  spotCostPerCpu: 0,
  spotCostPerGib: 0,
  spotCpuCount: 0,
  spotUptime: 0,
  spotCpuCost: 0,
  spotRamCost: 0,
  spotRamGib: 0,
  spotCostPerGpu: 0,
  spotGpuCount: 0,
  spotGpuCost: 0,

  totalTpu: 0,
  totalTpuCost: 0,
  normalizedCostPerTpu: 0,
  onDemandCostPerTpu: 0,
  onDemandTpuCount: 0,
  onDemandTpuCost: 0,
  fallbackCostPerTpu: 0,
  fallbackTpuCount: 0,
  fallbackTpuCost: 0,
  spotCostPerTpu: 0,
  spotTpuCount: 0,
  spotTpuCost: 0,
};

/**
 * Partitions data points by day, identifying today's data points and fallback data points
 */
const partitionDataPointsByDay = (
  dataPoints: HourlyClusterCostReportData[],
  timezone: string
): Record<string, CostReportDataByPeriod[]> => {
  const partitionedDataPointsContainer: Record<
    string,
    CostReportDataByPeriod[]
  > = {
    today: [],
    todayFallback: [],
  };

  return dataPoints.reduce((acc, datapoint) => {
    if (isCurrentDayDatapoint(datapoint, timezone)) {
      acc.today.push(datapoint);

      if (hasFallbackDataPoint(datapoint)) {
        acc.todayFallback.push(datapoint);
      }
    }

    return acc;
  }, partitionedDataPointsContainer);
};

type CalculateMonthlyForecastParams = {
  data: CostReportDataByPeriod[];
  todayProjected: number;
  timezone: string;
  granularity: DataGranularity;
};

const calculateMonthlyForecast = ({
  data,
  todayProjected,
  timezone,
  granularity,
}: CalculateMonthlyForecastParams): number => {
  if (!data.length) {
    return 0;
  }

  const multiplier = granularityToSeconds(granularity) / SECONDS_IN_HOUR;

  return (
    (sumBy(
      data.filter(({ timestamp }) => isPastDataPoint(timestamp, timezone)),
      'totalCost'
    ) || 0) *
      multiplier +
    todayProjected
  );
};

type AggregateClusterMetricsParams = {
  currentData: HourlyClusterCostReportData[];
  previousData: HourlyClusterCostReportData[];
  currentSummary?: CostReportSummary;
  previousSummary?: CostReportSummary;
  timezone: string;
  isDefaultFilter: boolean;
  granularity: DataGranularity;
};

export const aggregateClusterMetrics = ({
  currentData,
  previousData,
  currentSummary = emptySummary,
  previousSummary = emptySummary,
  timezone,
  isDefaultFilter,
  granularity,
}: AggregateClusterMetricsParams): AggregatedMetrics => {
  const partitionedDataPoints = partitionDataPointsByDay(currentData, timezone);
  const currentOngoing = getAverageRates(
    partitionedDataPoints.today,
    partitionedDataPoints.todayFallback
  );

  const onDemandProjected = toBig(currentOngoing.onDemandCost)
    .mul(HOURS_IN_DAY)
    .toNumber();

  const fallbackProjected = toBig(currentOngoing.fallbackCost)
    .mul(HOURS_IN_DAY)
    .toNumber();
  const spotProjected = toBig(currentOngoing.spotCost)
    .mul(HOURS_IN_DAY)
    .toNumber();
  const storageProjected = toBig(currentOngoing.totalStorageCost)
    .mul(HOURS_IN_DAY)
    .toNumber();

  const todayProjected = toBig(onDemandProjected)
    .add(fallbackProjected)
    .add(spotProjected)
    .add(storageProjected)
    .toNumber();

  const computeCost = {
    hourly: toBig(currentSummary.avgCost).toNumber(),
    daily: toBig(currentSummary.avgCost).mul(HOURS_IN_DAY).toNumber(),
    total: 0, // not used in cluster compute cost but exists in shared type.
    todayProjected,
    onDemandProjected,
    fallbackProjected,
    spotProjected,
    storageProjected,
    rangeSoFar: toBig(currentSummary.totalCost).add(todayProjected).toNumber(),
    onDemandRangeSoFar: toBig(currentSummary.totalCostOnDemand)
      .add(onDemandProjected)
      .toNumber(),
    fallbackRangeSoFar: toBig(currentSummary.totalCostSpotFallback)
      .add(fallbackProjected)
      .toNumber(),
    spotRangeSoFar: toBig(currentSummary.totalCostSpot)
      .add(spotProjected)
      .toNumber(),
    storageRangeSoFar: toBig(currentSummary.totalStorageCost)
      .add(storageProjected)
      .toNumber(),
  };

  const hourlyCostPerCpu: HourlyCostPerResource = {
    normalizedSoFar: toBig(currentOngoing.normalizedCostPerCpu).toNumber(),
    normalizedAverage: toBig(currentSummary.avgCpuCost).toNumber(),

    onDemandSoFar: toBig(currentOngoing.onDemandCostPerCpu).toNumber(),
    onDemandAverage: toBig(currentSummary.avgCpuCostOnDemand).toNumber(),

    fallbackSoFar: toBig(currentOngoing.fallbackCostPerCpu).toNumber(),
    fallbackAverage: toBig(currentSummary.avgCpuCostSpotFallback).toNumber(),

    spotSoFar: toBig(currentOngoing.spotCostPerCpu).toNumber(),
    spotAverage: toBig(currentSummary.avgCpuCostSpot).toNumber(),
  };

  const hourlyCostPerRamGib: HourlyCostPerResource = {
    normalizedSoFar: toBig(currentOngoing.normalizedCostPerGib).toNumber(),
    normalizedAverage: toBig(currentSummary.avgRamCost).toNumber(),

    onDemandSoFar: toBig(currentOngoing.onDemandCostPerGib).toNumber(),
    onDemandAverage: toBig(currentSummary.avgRamCostOnDemand).toNumber(),

    fallbackSoFar: toBig(currentOngoing.fallbackCostPerGib).toNumber(),
    fallbackAverage: toBig(currentSummary.avgRamCostSpotFallback).toNumber(),

    spotSoFar: toBig(currentOngoing.spotCostPerGib).toNumber(),
    spotAverage: toBig(currentSummary.avgRamCostSpot).toNumber(),
  };

  const hourlyCostPerGpu: HourlyCostPerResource = {
    normalizedSoFar: toBig(currentOngoing.normalizedCostPerGpu).toNumber(),
    normalizedAverage: toBig(currentSummary.avgGpuCost).toNumber(),

    onDemandSoFar: toBig(currentOngoing.onDemandCostPerGpu).toNumber(),
    onDemandAverage: toBig(currentSummary.avgGpuCostOnDemand).toNumber(),

    fallbackSoFar: toBig(currentOngoing.fallbackCostPerGpu).toNumber(),
    fallbackAverage: toBig(currentSummary.avgGpuCostSpotFallback).toNumber(),

    spotSoFar: toBig(currentOngoing.spotCostPerGpu).toNumber(),
    spotAverage: toBig(currentSummary.avgGpuCostSpot).toNumber(),
  };

  const hourlyCostPerStorage: HourlyCostPerResource = {
    normalizedSoFar: toBig(currentOngoing.costPerStorage).toNumber(),
    normalizedAverage: toBig(currentSummary.avgStorageCost).toNumber(),

    onDemandSoFar: 0,
    onDemandAverage: 0,

    fallbackSoFar: 0,
    fallbackAverage: 0,

    spotSoFar: 0,
    spotAverage: 0,
  };

  const currentAvgCpuCostPerUnit = safeDivide(
    currentSummary.avgCpuCost,
    currentSummary.avgCpuCount
  );
  const currentAvgRamCostPerUnit = safeDivide(
    currentSummary.avgRamCost,
    currentSummary.avgRamGib
  );
  const currentAvgGpuCostPerUnit = safeDivide(
    currentSummary.avgGpuCost,
    currentSummary.avgGpuCount
  );
  const currentAvgStorageCostPerUnit = safeDivide(
    currentSummary.avgStorageCost,
    currentSummary.avgStorageGib
  );

  return {
    computeCost,
    hourlyCostPerCpu,
    hourlyCostPerGpu,
    hourlyCostPerRamGib,
    hourlyCostPerStorage,

    cpuCount: {
      total: toBig(currentSummary.avgCpuCountOnDemand)
        .add(currentSummary.avgCpuCountSpot)
        .add(currentSummary.avgCpuCountSpotFallback)
        .toNumber(),
      onDemand: toBig(currentSummary.avgCpuCountOnDemand).toNumber(),
      fallback: toBig(currentSummary.avgCpuCountSpotFallback).toNumber(),
      spot: toBig(currentSummary.avgCpuCountSpot).toNumber(),
    },

    monthlyForecast: {
      cost: isDefaultFilter
        ? calculateMonthlyForecast({
            data: currentData,
            todayProjected,
            timezone,
            granularity,
          })
        : currentSummary.avgCost * AVERAGE_HOURS_IN_MONTH,
      prevCost:
        isDefaultFilter && previousSummary.totalCost
          ? calculateMonthlyForecast({
              data: previousData,
              todayProjected: 0,
              timezone,
              granularity,
            })
          : undefined,
    },

    avgDailyCost: {
      cost: toBig(currentSummary.avgCost).mul(HOURS_IN_DAY).toNumber(),
      prevCost:
        isDefaultFilter && currentSummary.avgCost && previousSummary.avgCost
          ? toBig(previousSummary.avgCost).mul(HOURS_IN_DAY).toNumber()
          : undefined,
    },

    avgDailyCostPerCpu: toBig(currentAvgCpuCostPerUnit)
      .mul(HOURS_IN_DAY)
      .toNumber(),
    avgDailyCostPerGib: toBig(currentAvgRamCostPerUnit)
      .mul(HOURS_IN_DAY)
      .toNumber(),
    avgDailyCostPerGpu: toBig(currentAvgGpuCostPerUnit)
      .mul(HOURS_IN_DAY)
      .toNumber(),
    avgDailyCostPerStorage: toBig(currentAvgStorageCostPerUnit)
      .mul(HOURS_IN_DAY)
      .toNumber(),
  };
};
